/* eslint react-hooks/exhaustive-deps: "warn" */

import { useContext } from 'react';

import { ContentContext } from 'context/ContentContext';

import { useInt } from 'hooks/useInt';

import { type DataCases, UseCases } from 'ui/organisms/UseCases/UseCases';
import { FeatureCards } from 'ui/organisms/FeatureCards/FeatureCards';
import { FeaturedContent } from 'ui/organisms/FeaturedContent/FeaturedContent';
import { CustomerSuccess } from 'ui/organisms/CustomerSuccess/CustomerSuccess';
import { GetStarted } from 'ui/organisms/GetStarted/GetStarted';
import { DetailsBlock } from 'ui/molecules/DetailsBlock/DetailsBlock';
import { LocationOverview } from 'ui/molecules/LocationOverview/LocationOverview';
import { BlockStandards } from 'ui/molecules/BlockStandards/BlockStandards';
import { AwardsBlock } from 'ui/molecules/AwardsBlock/AwardsBlock';
import { DemoBlock } from 'ui/molecules/DemoBlock/DemoBlock';
import { VoicesBlock } from 'ui/molecules/VoicesBlock/VoicesBlock';
import { Button } from 'ui/atoms/Button/Button';
import { Faq } from 'ui/atoms/Faq/Faq';

import styles from './LocationDataPage.module.scss';

const baseClass = 'location-data';

const LocationDataPage = () => {
  const { additionals, secondaryNavigation, pageVars } = useContext(ContentContext);

  const { t } = useInt();

  const dataCases: DataCases[] = [
    {
      title: t('E-commerce'),
      description: t(
        'Improve checkout experiences with real-time address validation, reducing cart abandonment and increasing conversion rates.',
      ),
      icon: 'commerce',
    },
    {
      title: t('Marketing'),
      description: t(
        'Boost campaign effectiveness with targeted outreach based on accurate demographic and geographic data.',
      ),
      icon: 'marketing',
    },
    {
      title: t('Financial Services'),
      description: t(
        'Accelerate customer onboarding and meet compliance requirements with verified and standardized address information.',
      ),
      icon: 'financial',
    },
    {
      title: t('Logistics and Delivery'),
      description: t(
        'Enhance fleet efficiency and delivery success rates with precise geocoding and route optimization.',
      ),
      icon: 'delivery',
    },
    {
      title: t('Real Estate'),
      description: t('Access detailed property and neighborhood data to inform investments and client advisories.'),
      icon: 'estate',
    },
  ];

  return (
    <div className={styles[`${baseClass}-wrapper`]}>
      <DetailsBlock
        iconType="info-solutions-location"
        title={t('Global Address Verification Data, Straight from the Source')}
        description={t(
          'As a first-party provider, Global Database independently gathers and standardizes address data from 200 countries, ensuring your data is always accurate and up-to-date.',
        )}
        actions={
          <Button size="xxl" route={secondaryNavigation.book_demo.slug} type="dark">
            {pageVars.common.book_demo_button}
          </Button>
        }
      />

      <LocationOverview
        title={t('Overview of Location Data')}
        description={t(
          'At Global Database, we provide accurate, up-to-date location data spanning 200 countries. By sourcing, standardizing, and translating data from diverse government registries, we ensure a seamless experience for businesses that rely on trusted, consistent location data to drive logistics, e-commerce, financial services, and more.',
        )}
      />

      <FeatureCards
        title={t('Accurate Global Address Verification, Directly from the Source')}
        description={t(
          'Global Database, as a first-party provider, independently collects and standardizes address data from 200 countries, ensuring your information remains precise and current.',
        )}
      />

      <UseCases
        title={t('Use Cases')}
        layout="grid-3-2"
        dataCases={dataCases}
        actionBlock={
          <div className={styles[`${baseClass}-use-cases-action`]}>
            <h3>{t('Ready to Unlock the Power of Location Data?')}</h3>
            <p>{t('See our platform in action and explore how it can benefit your business.')}</p>

            <Button
              size="xxl"
              type="white"
              route={secondaryNavigation.book_demo.slug}
              className={styles[`${baseClass}-use-cases-cta`]}
            >
              {pageVars.common.book_demo_button}
            </Button>
          </div>
        }
      />

      <CustomerSuccess
        kicker="Customer Success Stories"
        title={t('Tales of Triumph')}
        description={t(
          'Journey through the stories of those who turned challenges into victories, guided by the power of our partnership. Success lives in every chapter.',
        )}
      />

      <DemoBlock
        title={t(
          'Request a demo to experience the power of our global sanction screening solution and enhance your compliance program.',
        )}
      />

      {additionals.faq && additionals.faq.data.length > 0 && (
        <Faq title={t('Frequently asked questions')} data={additionals.faq.data} />
      )}

      <FeaturedContent
        title={t('Expert Insights for Business Leaders')}
        description={t(
          'Browse the latest thought leadership, best practices, and in-depth insights from Dun & Bradstreet. From exploring the latest way to assess credit risk to utilizing master data, our experts introduce you to cutting-edge ideas that are impacting companies across diverse industries',
        )}
      />

      <AwardsBlock
        title={t('Intelligent Data, Limitless Possibilities')}
        description={t(
          'See why Global Database is celebrated on G2 for driving success and innovation across companies large and small.',
        )}
      />

      <BlockStandards title={t("We Adhere to the Industry's Highest Standards of Excellence")} />

      <VoicesBlock
        title={t('Voices on Global Database')}
        description={t(
          'Hear how Global Database has empowered businesses worldwide, transforming data into opportunities and insights into success.',
        )}
      />

      <GetStarted
        title={t('Get Started Today')}
        description={t(
          'Unlock the potential of accurate global location data. Contact us to learn how Global Database can transform your business.',
        )}
      />
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default LocationDataPage;
